import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import api from '../services/api'
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineIdentification,
  HiOutlineUserGroup,
  HiOutlinePencil,
  HiOutlineKey,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineBookmark,
  HiOutlineChevronRight,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import clsx from 'clsx'

function Profile() {
  const { user, refreshUser } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
  })

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await api.put(`/users/${user.id}`, formData)

      if (response.data.status === 'success') {
        toast.success('Cập nhật thành công!')
        refreshUser()
        setIsEditing(false)
      }
    } catch (error) {
      toast.error('Cập nhật thất bại')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Mật khẩu mới không khớp')
      return
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Mật khẩu phải có ít nhất 6 ký tự')
      return
    }

    setLoading(true)

    try {
      const response = await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      })

      if (response.data.status === 'success') {
        toast.success('Đổi mật khẩu thành công!')
        setIsChangingPassword(false)
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        })
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Đổi mật khẩu thất bại')
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin', class: 'badge-danger' },
      leader: { label: 'Leader', class: 'badge-warning' },
      sales: { label: 'Sales', class: 'badge-primary' },
    }
    return badges[role] || badges.sales
  }

  const roleBadge = getRoleBadge(user?.role)

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Thông tin cá nhân</h1>
        <p className="text-gray-500 mt-1">Quản lý thông tin tài khoản của bạn</p>
      </div>

      {/* Profile card */}
      <div className="card overflow-hidden">
        {/* Avatar section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-8">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
              <span className="text-3xl font-bold text-primary-600">
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="text-white">
              <h2 className="text-xl font-semibold">{user?.full_name}</h2>
              <p className="text-primary-100">{user?.email}</p>
              <span className={clsx('mt-2 inline-block', roleBadge.class)}>
                {roleBadge.label}
              </span>
            </div>
          </div>
        </div>

        {/* Info section */}
        <div className="p-6">
          {isEditing ? (
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Họ và tên
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Số điện thoại
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1"
                >
                  <HiOutlineCheck className="w-5 h-5 mr-2" />
                  Lưu thay đổi
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                >
                  <HiOutlineX className="w-5 h-5 mr-2" />
                  Hủy
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <HiOutlineIdentification className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Mã nhân viên</p>
                  <p className="font-medium text-gray-900">{user?.employee_id}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <HiOutlineUser className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Họ và tên</p>
                  <p className="font-medium text-gray-900">{user?.full_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <HiOutlineMail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{user?.email || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 py-3 border-b border-gray-100">
                <HiOutlinePhone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Số điện thoại</p>
                  <p className="font-medium text-gray-900">{user?.phone || '-'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 py-3">
                <HiOutlineUserGroup className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Team</p>
                  <p className="font-medium text-gray-900">
                    {user?.team?.name || 'Chưa phân team'}
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-outline w-full"
                >
                  <HiOutlinePencil className="w-5 h-5 mr-2" />
                  Chỉnh sửa thông tin
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick links section */}
      <div className="card mt-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Truy cập nhanh</h3>
        </div>
        <div className="divide-y divide-gray-100">
          <Link
            to="/saved"
            className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <HiOutlineBookmark className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Tin đã lưu</p>
                <p className="text-sm text-gray-500">Xem các tin tức bạn đã đánh dấu</p>
              </div>
            </div>
            <HiOutlineChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        </div>
      </div>

      {/* Change password section */}
      <div className="card mt-6">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <HiOutlineKey className="w-5 h-5 mr-2 text-gray-400" />
            Đổi mật khẩu
          </h3>

          {isChangingPassword ? (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mật khẩu hiện tại
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      current_password: e.target.value,
                    })
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mật khẩu mới
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      new_password: e.target.value,
                    })
                  }
                  className="input"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Xác nhận mật khẩu mới
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirm_password: e.target.value,
                    })
                  }
                  className="input"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1"
                >
                  Đổi mật khẩu
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false)
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: '',
                    })
                  }}
                  className="btn-secondary"
                >
                  Hủy
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setIsChangingPassword(true)}
              className="btn-outline"
            >
              Đổi mật khẩu
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Profile
